/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener('DOMContentLoaded', function() {
  /**
   * @meta URL campaign
   * @description grabs url campaign key and id from URL and stores it in cart attributes
   * @returns {void}
   */
  const setupDynamicCampaignCode = () => {
    const cartCampaignForm = document.getElementById('cart-campaign-form');
    if (!cartCampaignForm) return;
    
    const campaignInput = cartCampaignForm.querySelector('input[name="attributes[campaign_id]"]');
    const campaignNameInput = cartCampaignForm.querySelector('input[name="attributes[campaign_name]"]');
    if (!campaignInput || !campaignNameInput) return;
    
    const parameterKeys = campaignInput.getAttribute('data-parameter-key').split(',') || ['campaign'];
    
    // Get URL parameters    
    const urlParams = new URLSearchParams(window.location.search);
    
    const campaigns = parameterKeys.map(key => {
      const value = urlParams.get(key);
      return value !== null ? { name: key, id: value } : null;
    })
    .filter(entry => entry !== null);
    
    // we only allow one campaign to be active at a time.
    // can only set a new one after the current one has expired.
    const eligibleCampaign = campaigns[0];
    
    console.log({parameterKeys, campaigns, eligibleCampaign, urlParams, inputValue: campaignInput.value});
    
    const unixTimestampInSeconds = Date.now();
    const campaignTimestamp = parseInt(cartCampaignForm.querySelector('input[name="attributes[campaign_timestamp]"]')?.value || '0');
    const oneDayInMs = 24 * 60 * 60 * 1000;
    
    // If we have a campaign ID in the URL and it's different from the current one
    if (eligibleCampaign && eligibleCampaign.id !== campaignInput.value) {
      if (eligibleCampaign.id === 'disable' && eligibleCampaign.name === campaignNameInput.value) {
        // Disable the campaign code and clear timestamp
        fetch(theme.routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              campaign_name: '',
              campaign_id: '',
              campaign_timestamp: ''
            }
          })
        })
        .then(response => response.json())
        .then(() => {
          // Remove parameters from URL to prevent reload loop
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
          window.location.reload();
        })
        .catch((error) => {          
          console.error('Error disabling campaign ID:', error.message);
        });
      } else if (!campaignInput.value) {
        // Set the new campaign ID with timestamp
        fetch(theme.routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              campaign_name: eligibleCampaign.name,
              campaign_id: eligibleCampaign.id,
              campaign_timestamp: unixTimestampInSeconds
            }
          })
        })
        .then(response => response.json())
        .then(() => {
          // Remove parameters from URL to prevent reload loop
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, newUrl);
          window.location.reload();
        })
        .catch((error) => {          
          console.error('Error updating campaign ID:', error.message);
        });
      }
    } 
    // Check if existing campaign is expired (older than 24 hours)
    else if (campaignInput.value && campaignTimestamp > 0 && (unixTimestampInSeconds - campaignTimestamp) > oneDayInMs) {
      // Clear campaign if it's older than 24 hours
      fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            campaign_name: '',
            campaign_id: '',
            campaign_timestamp: ''
          }
        })
      })
      .then(response => response.json())
      .then(() => window.location.reload())
      .catch((error) => {
        console.error('Error clearing expired campaign ID:', error.message);
      });
    }
  }
  
  const setupCopyListener = () => {
    const copyElements = document.querySelectorAll('[data-copy]');

    copyElements.forEach(element => {
      element.addEventListener('click', (event) => {
        event.preventDefault();
        const codeSpan = element.querySelector('[data-copy-text]') || element;
        const code = codeSpan.textContent.replace(/\s/g, '');
        navigator.clipboard.writeText(code);
        
        const copyIcon = element.querySelector('[data-copy-icon]');
        const copiedIcon = element.querySelector('[data-copied-icon]');
        copyIcon.hidden = true;
        copiedIcon.hidden = false;
        
        setTimeout(() => {
          copyIcon.hidden = false;
          copiedIcon.hidden = true;
        }, 5000);
      });
    });
  }
  
  const openFilterToggleOnExternalClick = (event) => {
    const filterDrawer = document.querySelector('filter-container.filter-container');
    
    if (!filterDrawer) return;
    
    document.addEventListener('on:filter:toggle', (event) => {
      const origin = event.detail.origin;
      const target = origin.getAttribute('data-toggle-target');
      const filterOptionToggle = filterDrawer.querySelector(`[data-toggle-target="${target}"].toggle-target-toggler--is-hidden`);
      
      if (filterOptionToggle) {
        filterOptionToggle.click();
      }
    });
  }
  
  setupDynamicCampaignCode();
  setupCopyListener();
  openFilterToggleOnExternalClick();
});